import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import {
  CameraAnalysisMetadata,
  CameraFocus,
  CameraRequestMetadata,
  FaceDiagnosisResponse,
  DiagnosisStage,
  DiagnosisSessionResponse,
  DiagnosisExplanationCard,
  HerbSuggestion,
  Message,
  PrescriptionMetadata,
  PrescriptionValidationPayload,
  ProfileResponse,
  MedicalRecordDetail,
  PrescriptionSummary,
  PatientInfoPayload,
} from '../types';
import {
  API_BASE_URL,
  sendDiagnosisMessage,
  fetchCommunityKnowledge,
  startDiagnosisSession as startDiagnosisSessionRequest,
  updateDiagnosisSession as updateDiagnosisSessionRequest,
  fetchDiagnosisSession as fetchDiagnosisSessionRequest,
} from '../services/chatService';
import apiClient from '../services/apiClient';
import {
  fetchProfile,
  fetchMedicalRecords,
  fetchMedicalRecordDetail,
  fetchPrescriptions,
} from '../services/profileService';
import { transcribeVoice, synthesizeSpeech } from '../services/voiceService';
import './ChatInterface.css';

const VOICE_PLAYBACK_RATE = 1.2;
const VOICE_AWAITING_TIMEOUT_MS = 8000;
const ENABLE_ADVANCED_SESSION_PANEL = false;

const LEGACY_STAGE_ORDER: DiagnosisStage[] = [
  'collect_symptoms',
  'diagnosis_analysis',
  'plan',
  'completed',
];

const MODERN_STAGE_ORDER: DiagnosisStage[] = [
  'collect_symptoms',
  'diagnosis_analysis',
  'plan',
  'completed',
];

const LEGACY_STAGE_SET = new Set<DiagnosisStage>(LEGACY_STAGE_ORDER);
const MODERN_STAGE_SET = new Set<DiagnosisStage>([
  ...MODERN_STAGE_ORDER,
  'observe',
  'listen',
  'inquiry',
  'pulse',
  'analysis',
]);

const stageLabels: Record<DiagnosisStage, string> = {
  collect_symptoms: '症状采集',
  diagnosis_analysis: '辨证分析',
  herb_selection: '选配药材',
  completed: '诊疗完成',
  observe: '望诊',
  listen: '闻诊',
  inquiry: '问诊',
  pulse: '切诊',
  analysis: '辨证分析',
  plan: '调理方案',
};

const stageDescriptions: Record<DiagnosisStage, string> = {
  collect_symptoms: '请详细描述您的症状，便于我全面了解情况。',
  diagnosis_analysis: '根据症状辨别证型，综合判断病因病机。',
  herb_selection: '结合辨证结果，为您挑选恰当的药材组合。',
  completed: '问诊流程结束，可查看处方与调理建议。',
  observe: '聚焦面色、舌象与体态，记录初步观察线索。',
  listen: '关注声音、口气与体味变化，补充辅助诊断信息。',
  inquiry: '深入了解主诉、病程与伴随症状，完善四诊资料。',
  pulse: '结合脉象、体温等体征信息，弥补四诊闭环。',
  analysis: '对已收集信息进行辨证归纳，明确证候。',
  plan: '制定调理方案与随访计划，生成个性化建议。',
};

const STAGE_LABELS: Record<string, string> = {
  observe: '望诊',
  listen: '闻诊',
  inquiry: '问诊',
  pulse: '切诊',
  analysis: '辨证分析',
  plan: '调理方案',
  collect_symptoms: '症状采集',
  diagnosis_analysis: '诊断分析',
  herb_selection: '方药甄选',
  completed: '诊疗完成',
};

const DIAGNOSIS_STAGE_ORDER = [
  'collect_symptoms',
  'diagnosis_analysis',
  'plan',
  'completed',
  'observe',
  'listen',
  'inquiry',
  'pulse',
  'analysis',
];

const DEFAULT_STAGE: DiagnosisStage = 'collect_symptoms';

const FACT_LABELS: Record<string, string> = {
  appearance_summary: '望诊概要',
  listen_summary: '闻诊概要',
  symptoms_summary: '症状总结',
  lifestyle_notes: '生活方式记录',
  pulse_summary: '切诊记录',
  pulse_character: '脉象描述',
  syndrome_summary: '辨证结论',
  care_plan_markdown: '调理方案要点',
  follow_up_tasks: '随访任务',
};

const DEFAULT_FACT_ORDER = [
  'appearance_summary',
  'listen_summary',
  'symptoms_summary',
  'lifestyle_notes',
  'pulse_summary',
  'pulse_character',
  'syndrome_summary',
  'care_plan_markdown',
];

const formatFactLabel = (key: string) => FACT_LABELS[key] ?? key.replace(/_/g, ' ');
const formatStageLabel = (key: string) => STAGE_LABELS[key] ?? key.replace(/_/g, ' ');

const isObservationStage = (value?: DiagnosisStage | null) =>
  value === 'collect_symptoms' || value === 'observe';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const hasRenderableValue = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return true;
  }

  if (Array.isArray(value)) {
    return value.some((item) => hasRenderableValue(item));
  }

  if (isPlainObject(value)) {
    return Object.values(value).some((item) => hasRenderableValue(item));
  }

  return true;
};

const renderRichValue = (value: unknown): React.ReactNode => {
  if (value === null || value === undefined) {
    return <span className="session-empty">未提供</span>;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return <span className="session-empty">未提供</span>;
    }
    const lines = trimmed.split(/\n+/).map((line) => line.trim()).filter(Boolean);
    if (lines.length > 1) {
      return (
        <ul className="session-bullet-list">
          {lines.map((line, index) => (
            <li key={`line-${index}`}>{line}</li>
          ))}
        </ul>
      );
    }
    return <span>{trimmed}</span>;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return <span>{String(value)}</span>;
  }

  if (Array.isArray(value)) {
    const items = value.filter((item) => item !== null && item !== undefined);
    if (items.length === 0) {
      return <span className="session-empty">未提供</span>;
    }
    return (
      <ul className="session-bullet-list">
        {items.map((item, index) => (
          <li key={`array-${index}`}>{renderRichValue(item)}</li>
        ))}
      </ul>
    );
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value).filter(([, val]) => val !== null && val !== undefined && val !== '');
    if (entries.length === 0) {
      return <span className="session-empty">未提供</span>;
    }
    return (
      <div className="session-definition-list">
        {entries.map(([key, val]) => (
          <div className="session-definition-item" key={key}>
            <div className="session-definition-label">{formatFactLabel(key)}</div>
            <div className="session-definition-value">{renderRichValue(val)}</div>
          </div>
        ))}
      </div>
    );
  }

  return <span>{String(value)}</span>;
};

const createInitialAssistantMessage = (): Message => ({
  role: 'assistant',
  content:
    '您好，我是AI中医助手。本次问诊将依次完成症状采集、辨证分析、调理方案制定和诊疗完成确认。请先描述当前最困扰您的症状，我会逐步引导您补充关键信息。',
  timestamp: new Date().toISOString(),
  messageType: 'system',
});

const CHAT_STORAGE_KEY = 'ai_tcm_chat_session_v2';

const CAMERA_REQUEST_REGEX = /\{\{camera_request(?:\s+([^}]+))?\}\}/i;
const DEFAULT_CAMERA_INSTRUCTIONS =
  '请保持自然光线与正面角度，露出完整五官，拍摄一张正面面部照片。';
const DEFAULT_CAMERA_PROMPT =
  '这里是根据摄像头采集到的面部照片，请结合影像特征继续辨证分析。';

const CAMERA_FOCUS_CONFIG: Record<CameraFocus, {
  message: string;
  instructions: string;
  prompt: string;
}> = {
  face: {
    message: '为进一步观察整体气色与五官神态，我建议采集一张清晰的正面面部影像。',
    instructions: '请保持自然光线与正面角度，露出完整五官，拍摄一张正面面部照片。',
    prompt: '这里是根据摄像头采集到的面部照片，请结合面部气色与五官状态继续辨证分析。',
  },
  tongue: {
    message: '为了判断寒热虚实，我建议采集舌苔影像以辅助辨证。',
    instructions:
      '请在光线充足下轻伸舌头，保持稳定对焦，采集一张能看清舌苔厚薄与颜色的照片。',
    prompt: '这里是患者的舌苔影像，请结合舌色、舌苔厚薄与润燥情况进行辨证分析。',
  },
  skin: {
    message: '检测到皮肤相关症状，建议采集患处皮肤影像以观察局部变化。',
    instructions:
      '请将摄像头对准皮肤症状部位，保持清晰对焦与稳定光线，拍摄受影响区域的近距离照片。',
    prompt: '这里是皮肤局部影像，请结合颜色、肿胀与渗出情况进行辨证评估。',
  },
};

const CAMERA_FOCUS_LABELS: Record<CameraFocus, string> = {
  face: '面部',
  tongue: '舌苔',
  skin: '皮肤',
};

const inferCameraFocus = (
  diagnosisText?: string | null,
  latestUserInput?: string,
): CameraFocus => {
  const context = `${diagnosisText ?? ''} ${latestUserInput ?? ''}`;
  if (/[皮肤疹痘斑瘙痒红肿湿疹]/.test(context)) {
    return 'skin';
  }
  if (/[舌苔舌头口腔咽喉喉咙咳嗽流鼻涕感冒咽炎肺热痰湿寒热虚实]/.test(context)) {
    return 'tongue';
  }
  return 'face';
};

type CameraPermissionState = 'unknown' | 'prompt' | 'granted' | 'denied' | 'unsupported';

type ActiveCameraRequest = {
  requestId: string;
  focus: CameraFocus;
  instructions: string;
  prompt: string;
  autoOpen: boolean;
};

type CameraBannerAction = 'request' | 'retry';

type CameraBannerConfig = {
  tone: 'info' | 'warning';
  title: string;
  message: string;
  action?: CameraBannerAction;
  actionLabel?: string;
};

const generateRequestId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `camera-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const parseCameraRequest = (input: string): {
  sanitizedText: string;
  request: ActiveCameraRequest | null;
} => {
  const match = CAMERA_REQUEST_REGEX.exec(input);
  if (!match) {
    return { sanitizedText: input, request: null };
  }

  const rawParams = match[1]?.trim();
  const cleanedText = input.replace(match[0], '').trim();

  if (!rawParams || rawParams.length === 0) {
    return {
      sanitizedText: cleanedText || '我需要采集您的面部影像以辅助诊断。',
      request: {
        requestId: generateRequestId(),
        focus: 'face',
        instructions: DEFAULT_CAMERA_INSTRUCTIONS,
        prompt: DEFAULT_CAMERA_PROMPT,
        autoOpen: false,
      },
    };
  }

  const segments = rawParams
    .split(/[;|]/)
    .map((segment) => segment.trim())
    .filter(Boolean);

  const config: Record<string, string> = {};

  segments.forEach((segment) => {
    if (!segment.includes('=')) {
      config.instructions = segment;
      return;
    }
    const [rawKey, ...valueParts] = segment.split('=');
    const key = rawKey.trim().toLowerCase();
    const value = valueParts.join('=').trim().replace(/^"|"$/g, '');
    if (key) {
      config[key] = value;
    }
  });

  const focus = (config.focus as CameraFocus | undefined) ?? 'face';
  const instructions =
    config.instructions || config.instruction || DEFAULT_CAMERA_INSTRUCTIONS;
  const prompt = config.prompt || DEFAULT_CAMERA_PROMPT;
  const autoOpenRaw = config.auto || config.autoopen || config.auto_open;
  const autoOpen = typeof autoOpenRaw === 'string'
    ? ['true', '1', 'yes', 'auto'].includes(autoOpenRaw.toLowerCase())
    : false;

  return {
    sanitizedText: cleanedText || '我需要采集您的面部影像以辅助诊断。',
    request: {
      requestId: config.requestid || generateRequestId(),
      focus,
      instructions,
      prompt,
      autoOpen,
    },
  };
};

type PersistedChatState = {
  messages: Message[];
  conversationId: string | null;
  stage: DiagnosisStage;
  herbs: HerbSuggestion[];
  diagnosisSummary: string | null;
  latestPrescriptionId: string | null;
  diagnosisSessionId: string | null;
  prescriptionValidation: PrescriptionValidationPayload | null;
};

const restoreChatState = (): PersistedChatState | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CHAT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedChatState>;
    if (!parsed.messages || !Array.isArray(parsed.messages) || parsed.messages.length === 0) {
      return null;
    }
    return {
      messages: parsed.messages,
      conversationId: parsed.conversationId ?? null,
      stage: parsed.stage ?? DEFAULT_STAGE,
      herbs: parsed.herbs ?? [],
      diagnosisSummary: parsed.diagnosisSummary ?? null,
      latestPrescriptionId: parsed.latestPrescriptionId ?? null,
      diagnosisSessionId: parsed.diagnosisSessionId ?? null,
      prescriptionValidation: parsed.prescriptionValidation ?? null,
    };
  } catch (err) {
    console.warn('恢复聊天记录失败，将使用默认问诊开场。', err);
    return null;
  }
};

const ChatInterface: React.FC = () => {
  const log = useCallback((message: string, payload?: Record<string, unknown>) => {
    if (payload) {
      console.debug(`[ChatInterface] ${message}`, payload);
    } else {
      console.debug(`[ChatInterface] ${message}`);
    }
  }, []);

  const persisted = useMemo(() => {
    const restored = restoreChatState();
    if (restored) {
      log('restore:success', {
        messageCount: restored.messages.length,
        stage: restored.stage,
        hasConversation: Boolean(restored.conversationId),
      });
    } else {
      log('restore:empty');
    }
    return restored;
  }, [log]);
  const [messages, setMessages] = useState<Message[]>(() =>
    persisted?.messages ?? [createInitialAssistantMessage()],
  );
  const [conversationId, setConversationId] = useState<string | null>(
    persisted?.conversationId ?? null,
  );
  const [stage, setStage] = useState<DiagnosisStage>(
    persisted?.stage ?? DEFAULT_STAGE,
  );
  const stageOrder: DiagnosisStage[] = MODERN_STAGE_SET.has(stage)
    ? MODERN_STAGE_ORDER
    : LEGACY_STAGE_SET.has(stage)
    ? LEGACY_STAGE_ORDER
    : MODERN_STAGE_ORDER;
  const [herbs, setHerbs] = useState<HerbSuggestion[]>(persisted?.herbs ?? []);
  const [diagnosisSummary, setDiagnosisSummary] = useState<string | null>(
    persisted?.diagnosisSummary ?? null,
  );
  const [latestPrescriptionId, setLatestPrescriptionId] = useState<string | null>(
    persisted?.latestPrescriptionId ?? null,
  );
  const [diagnosisSessionId, setDiagnosisSessionId] = useState<string | null>(
    persisted?.diagnosisSessionId ?? null,
  );
  const [prescriptionValidation, setPrescriptionValidation] =
    useState<PrescriptionValidationPayload | null>(persisted?.prescriptionValidation ?? null);
  const [diagnosisSession, setDiagnosisSession] = useState<DiagnosisSessionResponse | null>(null);
  const [sessionFactsDraft, setSessionFactsDraft] = useState<Record<string, string>>({});
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    return window.localStorage.getItem('access_token');
  });
  const [patientProfile, setPatientProfile] = useState<ProfileResponse | null>(null);
  const [patientInfo, setPatientInfo] = useState<PatientInfoPayload | null>(null);
  const [patientContextLoading, setPatientContextLoading] = useState<boolean>(false);
  const [patientContextError, setPatientContextError] = useState<string | null>(null);
  const [latestRecord, setLatestRecord] = useState<MedicalRecordDetail | null>(null);
  const [recentPrescriptions, setRecentPrescriptions] = useState<PrescriptionSummary[]>([]);
  const [cameraPermission, setCameraPermission] = useState<CameraPermissionState>('unknown');
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [cameraUploading, setCameraUploading] = useState(false);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<{
    file: File;
    previewUrl: string;
    dataUrl: string;
  } | null>(null);
  const [activeCameraRequest, setActiveCameraRequest] = useState<ActiveCameraRequest | null>(null);
  const [autoCameraRequestIssued, setAutoCameraRequestIssued] = useState(false);
  const [cameraRequestDeclined, setCameraRequestDeclined] = useState(false);
  const [permissionBannerDismissed, setPermissionBannerDismissed] = useState(false);
  const [communitySearching, setCommunitySearching] = useState(false);
  const [chatInputValue, setChatInputValue] = useState('');

  type VoiceModePhase =
    | 'inactive'
    | 'initializing'
    | 'listening'
    | 'processing'
    | 'awaiting_playback'
    | 'speaking'
    | 'error';
  const [voiceModeEnabled, setVoiceModeEnabled] = useState(false);
  const [voiceModePhase, setVoiceModePhase] = useState<VoiceModePhase>('inactive');
  const [voiceModeError, setVoiceModeError] = useState<string | null>(null);
  const [voiceModeLevel, setVoiceModeLevel] = useState(0);
  const [voiceModeLastTranscript, setVoiceModeLastTranscript] = useState<string | null>(null);

  const voiceModeEnabledRef = useRef(false);
  const voiceModePhaseRef = useRef<VoiceModePhase>('inactive');
  const voiceModeStreamRef = useRef<MediaStream | null>(null);
  const voiceModeAudioContextRef = useRef<AudioContext | null>(null);
  const voiceModeAnalyserRef = useRef<AnalyserNode | null>(null);
  const voiceModeRecorderRef = useRef<MediaRecorder | null>(null);
  const voiceModeRafRef = useRef<number | null>(null);
  const voiceModeCollectingRef = useRef(false);
  const voiceModeSegmentChunksRef = useRef<Blob[]>([]);
  const voiceModeRecentChunksRef = useRef<Blob[]>([]);
  const voiceModeSegmentStartRef = useRef<number | null>(null);
  const voiceModeLastSpeechRef = useRef<number>(0);
  const voiceModeQueueRef = useRef<Blob[]>([]);
  const voiceModeProcessingRef = useRef(false);
  const voiceModeAwaitingTimerRef = useRef<number | null>(null);
  const voiceModeSettingsRef = useRef({
    silenceMs: 1300,
    threshold: 0.015,
    minUtteranceMs: 650,
    maxUtteranceMs: 20000,
  });
  const voiceModeDataArrayRef = useRef<Float32Array | null>(null);
  const voiceModeLastLevelUpdateRef = useRef<number>(0);
  const voiceModeActivatedAtRef = useRef<number | null>(null);
  const voiceModeLastSpokenKeyRef = useRef<string | null>(null);
  const voiceModeStartingRef = useRef(false);
  const processVoiceQueueRef = useRef<() => void>(() => {});
  const handleSendMessageRef = useRef<
    ((input: string, options?: { silent?: boolean; conversationIdOverride?: string | null }) => Promise<void>)
    | null
  >(null);

  useEffect(() => {
    voiceModeEnabledRef.current = voiceModeEnabled;
  }, [voiceModeEnabled]);

  useEffect(() => {
    voiceModePhaseRef.current = voiceModePhase;
  }, [voiceModePhase]);

  const clearVoiceAwaitingTimer = useCallback(() => {
    if (voiceModeAwaitingTimerRef.current !== null) {
      window.clearTimeout(voiceModeAwaitingTimerRef.current);
      voiceModeAwaitingTimerRef.current = null;
    }
  }, []);

  const scheduleVoiceAwaitingTimeout = useCallback(() => {
    clearVoiceAwaitingTimer();
    voiceModeAwaitingTimerRef.current = window.setTimeout(() => {
      if (!voiceModeEnabledRef.current) {
        return;
      }
      if (voiceModePhaseRef.current === 'awaiting_playback') {
        voiceModePhaseRef.current = 'listening';
        setVoiceModePhase('listening');
      }
    }, VOICE_AWAITING_TIMEOUT_MS);
  }, [clearVoiceAwaitingTimer]);

  type VoicePlaybackStatus = {
    status: 'idle' | 'loading' | 'ready' | 'playing' | 'error';
    audioUrl?: string;
    mimeType?: string;
    error?: string;
  };

  const [voicePlaybackStatuses, setVoicePlaybackStatuses] = useState<Record<string, VoicePlaybackStatus>>({});
  const [activeVoiceKey, setActiveVoiceKey] = useState<string | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const activeVoiceKeyRef = useRef<string | null>(null);
  const voicePlaybackStatusesRef = useRef<Record<string, VoicePlaybackStatus>>({});

  const buildMessageKey = useCallback((message: Message, index: number) => {
    if (message.timestamp) {
      return `${message.timestamp}-${index}`;
    }
    if (message.metadata && 'reportId' in (message.metadata as Record<string, unknown>)) {
      const reportId = (message.metadata as Record<string, unknown>).reportId;
      if (typeof reportId === 'string') {
        return `${reportId}-${index}`;
      }
    }
    return `msg-${index}`;
  }, []);

  const ensureAudioElement = useCallback(() => {
    if (!audioElementRef.current) {
      const audio = new Audio();
      audioElementRef.current = audio;

      audio.addEventListener('ended', () => {
        clearVoiceAwaitingTimer();
        const key = activeVoiceKeyRef.current;
        if (key) {
          setVoicePlaybackStatuses((prev) => {
            const current = prev[key];
            if (!current) {
              return prev;
            }
            return {
              ...prev,
              [key]: { ...current, status: 'ready' },
            };
          });
        }
        activeVoiceKeyRef.current = null;
        setActiveVoiceKey(null);
        if (voiceModeEnabledRef.current) {
          voiceModePhaseRef.current = 'listening';
          setVoiceModePhase('listening');
        }
      });

      audio.addEventListener('error', () => {
        clearVoiceAwaitingTimer();
        const key = activeVoiceKeyRef.current;
        if (key) {
          setVoicePlaybackStatuses((prev) => {
            const current = prev[key];
            if (!current) {
              return prev;
            }
            return {
              ...prev,
              [key]: {
                ...current,
                status: 'error',
                error: '音频播放失败，请稍后重试。',
              },
            };
          });
        }
        activeVoiceKeyRef.current = null;
        setActiveVoiceKey(null);
        if (voiceModeEnabledRef.current) {
          setVoiceModeError('音频播放失败，请稍后重试。');
          voiceModePhaseRef.current = 'listening';
          setVoiceModePhase('listening');
        }
      });
    }
    return audioElementRef.current!;
  }, [clearVoiceAwaitingTimer]);

  const stopVoicePlayback = useCallback(() => {
    const audio = audioElementRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    const key = activeVoiceKeyRef.current;
    if (key) {
      setVoicePlaybackStatuses((prev) => {
        const current = prev[key];
        if (!current) {
          return prev;
        }
        return {
          ...prev,
          [key]: { ...current, status: 'ready' },
        };
      });
    }
    activeVoiceKeyRef.current = null;
    setActiveVoiceKey(null);
    if (voiceModeEnabledRef.current) {
      clearVoiceAwaitingTimer();
      voiceModePhaseRef.current = 'listening';
      setVoiceModePhase('listening');
    }
  }, [clearVoiceAwaitingTimer]);

  const playVoiceFromUrl = useCallback(
    async (key: string, url: string) => {
      const audio = ensureAudioElement();

      if (activeVoiceKeyRef.current && activeVoiceKeyRef.current !== key) {
        stopVoicePlayback();
      }

    audio.src = url;
    audio.playbackRate = VOICE_PLAYBACK_RATE;

      try {
        await audio.play();
        activeVoiceKeyRef.current = key;
        setActiveVoiceKey(key);
        if (voiceModeEnabledRef.current) {
          clearVoiceAwaitingTimer();
          setVoiceModeError(null);
          voiceModePhaseRef.current = 'speaking';
          setVoiceModePhase('speaking');
        }
        setVoicePlaybackStatuses((prev) => {
          const current = prev[key];
          if (!current) {
            return {
              ...prev,
              [key]: { status: 'playing', audioUrl: url },
            };
          }
          return {
            ...prev,
            [key]: { ...current, status: 'playing' },
          };
        });
      } catch (err) {
        console.error('语音播放失败', err);
        setVoicePlaybackStatuses((prev) => ({
          ...prev,
          [key]: {
            status: 'error',
            error: err instanceof Error ? err.message : '语音播放失败，请稍后重试。',
          },
        }));
        if (voiceModeEnabledRef.current) {
          setVoiceModeError(err instanceof Error ? err.message : '语音播放失败，请稍后重试。');
          voiceModePhaseRef.current = 'listening';
          setVoiceModePhase('listening');
        }
      }
    },
  [clearVoiceAwaitingTimer, ensureAudioElement, stopVoicePlayback],
  );

  const handleToggleVoicePlayback = useCallback(
    async (message: Message, key: string) => {
      if (!message.content?.trim()) {
        return;
      }

      const currentStatus = voicePlaybackStatusesRef.current[key];

      if (activeVoiceKeyRef.current === key && currentStatus?.status === 'playing') {
        stopVoicePlayback();
        return;
      }

      if (currentStatus?.status === 'ready' && currentStatus.audioUrl) {
        await playVoiceFromUrl(key, currentStatus.audioUrl);
        return;
      }

      setVoicePlaybackStatuses((prev) => ({
        ...prev,
        [key]: {
          status: 'loading',
          audioUrl: prev[key]?.audioUrl,
          mimeType: prev[key]?.mimeType,
        },
      }));

      try {
        const { blob, mimeType } = await synthesizeSpeech(message.content);
        const newUrl = URL.createObjectURL(blob);
        setVoicePlaybackStatuses((prev) => {
          const previous = prev[key];
          if (previous?.audioUrl && previous.audioUrl !== newUrl) {
            URL.revokeObjectURL(previous.audioUrl);
          }
          return {
            ...prev,
            [key]: {
              status: 'ready',
              audioUrl: newUrl,
              mimeType,
            },
          };
        });
        await playVoiceFromUrl(key, newUrl);
      } catch (err) {
        console.error('语音合成失败', err);
        const messageText =
          err instanceof Error ? err.message : '语音合成失败，请稍后重试。';
        setVoicePlaybackStatuses((prev) => ({
          ...prev,
          [key]: {
            status: 'error',
            error: messageText,
          },
        }));
      }
    },
    [playVoiceFromUrl, stopVoicePlayback],
  );

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cameraReadyTimeoutRef = useRef<number | null>(null);
  const hasCameraEvidenceRef = useRef(false);
  const communitySearchRequestIdRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);
  const sessionInitializedRef = useRef(false);
  const lastSyncedSessionFactsRef = useRef<{ diagnosis: string; herbs: string }>({
    diagnosis: '',
    herbs: '',
  });

  useEffect(() => {
    hasCameraEvidenceRef.current = messages.some(
      (msg) => msg.messageType === 'image' || msg.messageType === 'image_analysis',
    );
    log('messages:update', { count: messages.length });
  }, [log, messages]);

  useEffect(() => {
    voicePlaybackStatusesRef.current = voicePlaybackStatuses;
  }, [voicePlaybackStatuses]);

  useEffect(() => {
    if (!voiceModeEnabledRef.current) {
      return;
    }

    const phase = voiceModePhaseRef.current;
    if (phase === 'initializing' || phase === 'processing' || phase === 'speaking' || phase === 'error') {
      return;
    }

    const latestAssistant = [...messages]
      .map((message, index) => ({ message, index }))
      .reverse()
      .find(({ message }) => {
        if (message.role !== 'assistant') {
          return false;
        }
        const content = message.content?.trim();
        if (!content) {
          return false;
        }
        const type = message.messageType ?? 'tcm_response';
        return !['camera_request', 'image', 'image_analysis', 'system'].includes(type);
      });

    if (!latestAssistant) {
      return;
    }

    const key = buildMessageKey(latestAssistant.message, latestAssistant.index);
    if (voiceModeLastSpokenKeyRef.current === key) {
      return;
    }

    voiceModeLastSpokenKeyRef.current = key;

    void (async () => {
      try {
        await handleToggleVoicePlayback(latestAssistant.message, key);
      } catch (error) {
        console.error('自动语音播报失败', error);
        voiceModeLastSpokenKeyRef.current = null;
        if (voiceModeEnabledRef.current) {
          setVoiceModeError(error instanceof Error ? error.message : '语音播报失败，请稍后重试。');
          voiceModePhaseRef.current = 'listening';
          setVoiceModePhase('listening');
        }
      }
    })();
  }, [buildMessageKey, handleToggleVoicePlayback, messages]);


  useEffect(() => {
    const validKeys = new Set(
      messages.map((message, index) => buildMessageKey(message, index)),
    );
    setVoicePlaybackStatuses((prev) => {
      let changed = false;
      const next: Record<string, VoicePlaybackStatus> = {};
      Object.entries(prev).forEach(([key, status]) => {
        if (validKeys.has(key)) {
          next[key] = status;
        } else {
          changed = true;
          if (status.audioUrl) {
            URL.revokeObjectURL(status.audioUrl);
          }
          if (activeVoiceKeyRef.current === key) {
            stopVoicePlayback();
          }
        }
      });
      return changed ? next : prev;
    });
  }, [buildMessageKey, messages, stopVoicePlayback]);

  useEffect(() => {
    return () => {
      stopVoicePlayback();
      const statuses = voicePlaybackStatusesRef.current;
      Object.values(statuses).forEach((status) => {
        if (status.audioUrl) {
          URL.revokeObjectURL(status.audioUrl);
        }
      });
    };
  }, [stopVoicePlayback]);

  const cameraBannerConfig = useMemo(() => {
    if (permissionBannerDismissed) {
      return null;
    }

    const shouldDisplayBanner =
      cameraPermission === 'unsupported' || cameraModalOpen || Boolean(activeCameraRequest);

    if (!shouldDisplayBanner) {
      return null;
    }

    if (cameraPermission === 'prompt') {
      return {
        tone: 'info',
        title: '等待开启摄像头权限',
        message:
          '首次采集影像时，请点击浏览器地址栏的“允许”按钮，使AI助手能够调用摄像头。',
        action: 'request' as CameraBannerAction,
        actionLabel: '立即授权',
      } satisfies CameraBannerConfig;
    }

    if (cameraPermission === 'denied') {
      return {
        tone: 'warning',
        title: '摄像头权限已关闭',
        message:
          '浏览器已拒绝摄像头访问。请在地址栏或系统设置中允许访问后点击“已完成授权”重试。',
        action: 'retry' as CameraBannerAction,
        actionLabel: '已完成授权，重新尝试',
      } satisfies CameraBannerConfig;
    }

    if (cameraPermission === 'unsupported') {
      return {
        tone: 'warning',
        title: '当前环境暂不支持摄像头调用',
        message:
          '请更换支持 WebRTC 的现代浏览器（如 Chrome、Edge、Safari 等）或接入可用的摄像头设备。',
      } satisfies CameraBannerConfig;
    }

    return null;
  }, [activeCameraRequest, cameraModalOpen, cameraPermission, permissionBannerDismissed]);

  useEffect(() => {
    isMountedRef.current = true;
    log('component:mount');
    if (typeof window === 'undefined') {
      log('component:ssr-skip');
      return () => {
        isMountedRef.current = false;
      };
    }
    const handleStorage = () => {
      setAccessToken(window.localStorage.getItem('access_token'));
      log('storage:accessTokenUpdated');
    };
    window.addEventListener('storage', handleStorage);
    return () => {
      isMountedRef.current = false;
      window.removeEventListener('storage', handleStorage);
      log('component:unmount');
    };
  }, [log]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraPermission('unsupported');
      return;
    }

    let cancelled = false;
    let permissionStatus: PermissionStatus | null = null;
    let handler: (() => void) | null = null;

    const mapPermission = (state: PermissionState): CameraPermissionState => {
      if (state === 'granted') return 'granted';
      if (state === 'denied') return 'denied';
      return 'prompt';
    };

    const queryPermission = async () => {
      if (!navigator.permissions || !(navigator.permissions as any).query) {
        setCameraPermission('prompt');
        return;
      }

      try {
        const status = await (navigator.permissions as any).query({ name: 'camera' });
        if (cancelled) return;
        permissionStatus = status;

        const update = () => {
          const mapped = mapPermission(status.state);
          setCameraPermission(mapped);
          if (mapped === 'granted') {
            setPermissionBannerDismissed(true);
          }
        };

        handler = update;
        update();
        status.addEventListener('change', update);
      } catch (error) {
        if (!cancelled) {
          setCameraPermission('prompt');
        }
      }
    };

    queryPermission();

    return () => {
      cancelled = true;
      if (permissionStatus && handler) {
        permissionStatus.removeEventListener('change', handler);
      }
    };
  }, []);

  useEffect(() => {
    if (cameraPermission === 'denied') {
      setPermissionBannerDismissed(false);
    }
  }, [cameraPermission]);

  useEffect(
    () => () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }
      if (cameraReadyTimeoutRef.current) {
        window.clearTimeout(cameraReadyTimeoutRef.current);
        cameraReadyTimeoutRef.current = null;
      }
      if (capturedImage?.previewUrl) {
        URL.revokeObjectURL(capturedImage.previewUrl);
      }
    },
    [cameraStream, capturedImage],
  );

  const activeStageIndex = useMemo(
    () => stageOrder.findIndex((item) => item === stage),
    [stage, stageOrder],
  );
  const hasAuth = Boolean(accessToken);
  const voiceLevelPercent = Math.min(Math.round(voiceModeLevel * 140), 100);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const payload: PersistedChatState = {
      messages,
      conversationId,
      stage,
      herbs,
      diagnosisSummary,
      latestPrescriptionId,
      diagnosisSessionId,
      prescriptionValidation,
    };
    try {
      window.localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(payload));
    } catch (err) {
      console.warn('保存聊天记录失败', err);
    }
  }, [
    messages,
    conversationId,
    stage,
    herbs,
    diagnosisSummary,
    latestPrescriptionId,
    diagnosisSessionId,
    prescriptionValidation,
  ]);

  const loadPatientContext = useCallback(async () => {
    if (!accessToken) {
      setPatientProfile(null);
      setPatientInfo(null);
      setLatestRecord(null);
      setRecentPrescriptions([]);
      setPatientContextError(null);
      setPatientContextLoading(false);
      return;
    }

    setPatientContextLoading(true);
    setPatientContextError(null);

    try {
      const profilePromise = fetchProfile();
      const recordsPromise = fetchMedicalRecords({ limit: 5 });
      const prescriptionsPromise = fetchPrescriptions({ limit: 5 });

      const [profileData, recordSummaries, prescriptionsData] = await Promise.all([
        profilePromise,
        recordsPromise,
        prescriptionsPromise,
      ]);

      let latestRecordDetail: MedicalRecordDetail | null = null;
      if (recordSummaries.length > 0) {
        try {
          latestRecordDetail = await fetchMedicalRecordDetail(recordSummaries[0].id);
        } catch (detailErr) {
          console.warn('获取最新病历详情失败', detailErr);
        }
      }

      setPatientProfile(profileData);
      setLatestRecord(latestRecordDetail);
      setRecentPrescriptions(prescriptionsData);

      const patientInfoPayload: PatientInfoPayload = {
        profile: {
          id: profileData.user.id,
          name: profileData.user.name ?? null,
          age: profileData.user.age ?? null,
          gender: profileData.user.gender ?? null,
          username: profileData.user.username ?? null,
          health_conditions: profileData.user.health_conditions ?? null,
        },
        latest_medical_record: latestRecordDetail
          ? {
              id: latestRecordDetail.id,
              summary: latestRecordDetail.summary ?? null,
              diagnosis: latestRecordDetail.diagnosis ?? null,
              symptoms: latestRecordDetail.symptoms ?? null,
              notes: latestRecordDetail.notes ?? null,
              created_at: latestRecordDetail.created_at ?? null,
              updated_at: latestRecordDetail.updated_at ?? null,
            }
          : null,
        recent_prescriptions: prescriptionsData.slice(0, 3).map((item) => ({
          id: item.id,
          prescription_id: item.prescription_id,
          title: item.title ?? null,
          created_at: item.created_at ?? null,
          diagnosis_summary: item.diagnosis_summary ?? null,
          symptoms_summary: item.symptoms_summary ?? null,
        })),
      };

      setPatientInfo(patientInfoPayload);
    } catch (err) {
      console.error('加载病历上下文失败', err);
      setPatientContextError('加载个人病历信息失败，请稍后再试。');
    } finally {
      setPatientContextLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadPatientContext();
  }, [loadPatientContext]);

  const buildInitialSessionFacts = useCallback((): Record<string, unknown> => {
    const facts: Record<string, unknown> = {};
    const profile = patientInfo?.profile;
    if (profile?.name) {
      facts.patient_name = profile.name;
    }
    if (typeof profile?.age === 'number') {
      facts.patient_age = profile.age;
    }
    if (profile?.gender) {
      facts.patient_gender = profile.gender;
    }
    if (profile?.health_conditions) {
      facts.health_conditions = profile.health_conditions;
    }
    if (latestRecord?.summary) {
      facts.symptoms_summary = latestRecord.summary;
    }
    if (latestRecord?.diagnosis) {
      facts.syndrome_summary = latestRecord.diagnosis;
    }
    if (latestRecord?.notes) {
      facts.lifestyle_notes = latestRecord.notes;
    }
    return facts;
  }, [patientInfo, latestRecord]);

  const syncSessionDraft = useCallback(
    (session: DiagnosisSessionResponse, options: { overwrite?: boolean } = {}) => {
      const candidateKeys = new Set<string>([
        ...session.required_facts,
        ...DEFAULT_FACT_ORDER,
      ]);
      Object.entries(session.facts || {}).forEach(([key, value]) => {
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          candidateKeys.add(key);
        }
      });

      setSessionFactsDraft((prev) => {
        const next: Record<string, string> = options.overwrite ? {} : { ...prev };
        candidateKeys.forEach((key) => {
          const value = session.facts?.[key];
          if (value === undefined || value === null) {
            return;
          }
          if (typeof value === 'string') {
            next[key] = value;
          } else if (typeof value === 'number' || typeof value === 'boolean') {
            next[key] = String(value);
          } else {
            try {
              next[key] = JSON.stringify(value, null, 2);
            } catch (err) {
              next[key] = String(value);
            }
          }
        });
        return next;
      });
    },
    [],
  );

  const initializeDiagnosisSession = useCallback(
    async (options: { forceReset?: boolean } = {}) => {
      const { forceReset = false } = options;
      if (sessionLoading && !forceReset) {
        return;
      }

      setSessionLoading(true);
      setSessionError(null);

      try {
        const response = await startDiagnosisSessionRequest({
          sessionId: forceReset ? null : diagnosisSessionId,
          facts: buildInitialSessionFacts(),
          forceReset,
          restore: true,
        });
        setDiagnosisSession(response);
        setDiagnosisSessionId(response.session_id);
        syncSessionDraft(response, { overwrite: true });
        sessionInitializedRef.current = true;
        log('diagnosis-session:init', {
          sessionId: response.session_id,
          stage: response.stage_id,
        });
      } catch (err) {
        console.error('初始化诊断阶段失败', err);
        setSessionError('初始化诊断流程失败，请稍后重试。');
        sessionInitializedRef.current = false;
      } finally {
        setSessionLoading(false);
      }
    },
    [
      buildInitialSessionFacts,
      diagnosisSessionId,
      log,
      sessionLoading,
      syncSessionDraft,
    ],
  );

  const handleRefreshSession = useCallback(async () => {
    if (!diagnosisSessionId) {
      await initializeDiagnosisSession({ forceReset: true });
      return;
    }

    setSessionLoading(true);
    setSessionError(null);

    try {
      const response = await fetchDiagnosisSessionRequest(diagnosisSessionId, { restore: true });
      setDiagnosisSession(response);
      syncSessionDraft(response);
    } catch (err) {
      console.error('刷新诊断阶段失败', err);
      setSessionError('刷新诊断阶段失败，请稍后再试。');
    } finally {
      setSessionLoading(false);
    }
  }, [diagnosisSessionId, initializeDiagnosisSession, syncSessionDraft]);

  const handleSessionFactChange = useCallback((key: string, value: string) => {
    setSessionFactsDraft((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const handleSubmitSessionFacts = useCallback(async () => {
    if (!diagnosisSessionId) {
      await initializeDiagnosisSession({ forceReset: true });
      return;
    }

    const factsPayload: Record<string, unknown> = {};
    Object.entries(sessionFactsDraft).forEach(([key, value]) => {
      const trimmed = value?.trim();
      if (!trimmed) {
        return;
      }
      if (
        (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))
      ) {
        try {
          factsPayload[key] = JSON.parse(trimmed);
          return;
        } catch (err) {
          console.warn('解析结构化诊断字段失败，按文本保存', err);
        }
      }
      factsPayload[key] = trimmed;
    });

    if (Object.keys(factsPayload).length === 0) {
      setSessionError('请先填写需要提交的诊断信息。');
      return;
    }

    setSessionLoading(true);
    setSessionError(null);

    try {
      const response = await updateDiagnosisSessionRequest({
        sessionId: diagnosisSessionId,
        facts: factsPayload,
      });
      setDiagnosisSession(response);
      syncSessionDraft(response);
      log('diagnosis-session:update', {
        sessionId: diagnosisSessionId,
        stage: response.stage_id,
        updatedFacts: Object.keys(factsPayload),
      });
    } catch (err) {
      console.error('提交诊断阶段信息失败', err);
      setSessionError('提交诊断信息失败，请稍后再试。');
    } finally {
      setSessionLoading(false);
    }
  }, [
    diagnosisSessionId,
    initializeDiagnosisSession,
    log,
    sessionFactsDraft,
    syncSessionDraft,
  ]);

  const handleAdvanceStage = useCallback(
    async (manualOverride: boolean) => {
      if (!diagnosisSessionId) {
        await initializeDiagnosisSession({ forceReset: true });
        return;
      }

      setSessionLoading(true);
      setSessionError(null);

      try {
        const response = await updateDiagnosisSessionRequest({
          sessionId: diagnosisSessionId,
          advance: true,
          manualOverride,
        });
        setDiagnosisSession(response);
        syncSessionDraft(response);
        log('diagnosis-session:advance', {
          sessionId: diagnosisSessionId,
          stage: response.stage_id,
          manualOverride,
        });
      } catch (err) {
        console.error('推进诊断阶段失败', err);
        setSessionError(
          manualOverride
            ? '强制推进失败，请补充阶段必需信息后再试。'
            : '推进阶段失败，可能需要先补充必填信息。',
        );
      } finally {
        setSessionLoading(false);
      }
    },
    [diagnosisSessionId, initializeDiagnosisSession, log, syncSessionDraft],
  );

  const syncDiagnosisSessionFacts = useCallback(
    async (facts: Record<string, unknown>, markers?: { diagnosis?: string | null; herbs?: HerbSuggestion[] }) => {
      if (!diagnosisSessionId || Object.keys(facts).length === 0) {
        return;
      }

      try {
        const response = await updateDiagnosisSessionRequest({
          sessionId: diagnosisSessionId,
          facts,
        });
        setDiagnosisSession(response);
        syncSessionDraft(response);
        if (markers) {
          if (markers.diagnosis !== undefined) {
            lastSyncedSessionFactsRef.current.diagnosis = markers.diagnosis ?? '';
          }
          if (markers.herbs) {
            lastSyncedSessionFactsRef.current.herbs = JSON.stringify(markers.herbs);
          }
        }
      } catch (err) {
        console.warn('同步诊断结构化信息失败', err);
      }
    },
    [diagnosisSessionId, syncSessionDraft],
  );

  useEffect(() => {
    if (sessionInitializedRef.current) {
      return;
    }
    if (patientContextLoading) {
      return;
    }
    sessionInitializedRef.current = true;
    void initializeDiagnosisSession({ forceReset: false });
  }, [initializeDiagnosisSession, patientContextLoading]);

  const buildCameraRequest = useCallback(
    (payload?: Partial<CameraRequestMetadata> | ActiveCameraRequest | null): ActiveCameraRequest => {
      if (!payload) {
        return {
          requestId: generateRequestId(),
          focus: 'face',
          instructions: DEFAULT_CAMERA_INSTRUCTIONS,
          prompt: DEFAULT_CAMERA_PROMPT,
          autoOpen: false,
        };
      }

      return {
        requestId: payload.requestId ?? generateRequestId(),
        focus: (payload.focus as CameraFocus | undefined) ?? 'face',
        instructions: payload.instructions ?? DEFAULT_CAMERA_INSTRUCTIONS,
        prompt: payload.prompt ?? DEFAULT_CAMERA_PROMPT,
        autoOpen: Boolean((payload as Partial<CameraRequestMetadata>).autoOpen ?? false),
      };
    },
    [],
  );

  const closeCameraStream = useCallback(() => {
    console.debug('[camera] closing stream');
    if (cameraReadyTimeoutRef.current) {
      window.clearTimeout(cameraReadyTimeoutRef.current);
      cameraReadyTimeoutRef.current = null;
    }
    if (cameraStream) {
      console.debug('[camera] stopping tracks', cameraStream.getTracks().map((track) => track.label));
      cameraStream.getTracks().forEach((track) => track.stop());
    }
    setCameraStream(null);
    setIsCameraReady(false);
    if (videoRef.current) {
      const video = videoRef.current;
      video.pause();
      video.srcObject = null;
    }
  }, [cameraStream]);

  useEffect(() => {
    if (!cameraModalOpen || !cameraStream) {
      return;
    }

    const video = videoRef.current;
    if (!video) {
      console.debug('[camera] modal open but video element not ready yet');
      return;
    }

    console.debug('[camera] attaching stream to video element');
    setIsCameraReady(false);
    video.srcObject = cameraStream;
    video.muted = true;
    video.setAttribute('playsinline', 'true');

    const markReady = () => {
      console.debug('[camera] video element reported readiness');
      setIsCameraReady(true);
    };

    video.addEventListener('canplay', markReady, { once: true });
    video.addEventListener('loadeddata', markReady, { once: true });

    const startPlayback = async () => {
      try {
        const playResult = video.play();
        if (playResult && typeof playResult.then === 'function') {
          await playResult;
        }
        console.debug('[camera] video playback promise resolved');
      } catch (playError) {
        console.warn('摄像头视频播放被阻止，将尝试使用静态帧。', playError);
      }
    };

    void startPlayback();

    if (cameraReadyTimeoutRef.current) {
      window.clearTimeout(cameraReadyTimeoutRef.current);
    }
    cameraReadyTimeoutRef.current = window.setTimeout(() => {
      if (cameraStream.active) {
        console.debug('[camera] readiness fallback fired after attach, stream active:', cameraStream.active);
        setIsCameraReady(true);
      }
      cameraReadyTimeoutRef.current = null;
    }, 800);

    return () => {
      video.removeEventListener('canplay', markReady);
      video.removeEventListener('loadeddata', markReady);
      if (cameraReadyTimeoutRef.current) {
        window.clearTimeout(cameraReadyTimeoutRef.current);
        cameraReadyTimeoutRef.current = null;
      }
    };
  }, [cameraModalOpen, cameraStream]);

  const openCameraStream = useCallback(async (): Promise<boolean> => {
    console.debug('[camera] requesting user media');
    setCameraError(null);
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraPermission('unsupported');
      setCameraError('当前浏览器不支持摄像头调用，请尝试更换或更新浏览器。');
      return false;
    }

    try {
      const constraints = { video: { width: 720, height: 480, facingMode: 'user' } };
      console.debug('[camera] constraints', constraints);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      closeCameraStream();
      console.debug('[camera] stream acquired', stream.getTracks().map((track) => `${track.kind}:${track.label}`));
      setCameraStream(stream);
      setIsCameraReady(false);
      setCameraPermission('granted');
      setPermissionBannerDismissed(true);

      if (videoRef.current) {
        const video = videoRef.current;
        video.srcObject = stream;
        video.muted = true;
        video.setAttribute('playsinline', 'true');
        const markReady = () => setIsCameraReady(true);

        video.addEventListener('canplay', markReady, { once: true });
        video.addEventListener('loadeddata', markReady, { once: true });

        try {
          const playResult = video.play();
          if (playResult && typeof playResult.then === 'function') {
            await playResult;
          }
          console.debug('[camera] video playback started');
          setIsCameraReady(true);
        } catch (playError) {
          console.warn('摄像头视频播放被阻止，将尝试使用静态帧。', playError);
        }

        if (cameraReadyTimeoutRef.current) {
          window.clearTimeout(cameraReadyTimeoutRef.current);
        }
        cameraReadyTimeoutRef.current = window.setTimeout(() => {
          if (stream.active) {
            setIsCameraReady(true);
          }
          console.debug('[camera] readiness fallback fired, stream active:', stream.active);
          cameraReadyTimeoutRef.current = null;
        }, 800);
      }
      return true;
    } catch (err) {
      const error = err as DOMException;
      let message = '无法访问摄像头，请检查权限设置。';
      if (error?.name === 'NotAllowedError') {
        message = '摄像头访问被拒绝，请在浏览器权限设置中允许访问。';
        setCameraPermission('denied');
        setPermissionBannerDismissed(false);
      } else if (error?.name === 'NotFoundError') {
        message = '未检测到可用的摄像头设备。';
      } else if (error?.name === 'NotReadableError') {
        message = '摄像头被其他应用占用或硬件异常。';
      } else if (error?.name === 'OverconstrainedError') {
        message = '摄像头暂不支持当前采集配置，请尝试降低分辨率。';
      }
      console.error('[camera] open failed', error);
      setCameraError(message);
      return false;
    }
  }, [closeCameraStream]);

  const resetCapturedImage = useCallback((options?: { reopenStream?: boolean }) => {
    setCameraError(null);
    setCameraUploading(false);
    if (capturedImage?.previewUrl) {
      URL.revokeObjectURL(capturedImage.previewUrl);
    }
    setCapturedImage(null);

    const shouldAttemptReopen = options?.reopenStream ?? true;

    if (shouldAttemptReopen && cameraModalOpen) {
      const stream = cameraStream;
      const streamInactive =
        !stream || !stream.active || stream.getTracks().every((track) => track.readyState === 'ended');

      if (streamInactive) {
        void openCameraStream();
      } else if (videoRef.current) {
        const video = videoRef.current;
        const playResult = video.play();
        if (playResult && typeof playResult.then === 'function') {
          playResult
            .then(() => setIsCameraReady(true))
            .catch(() => undefined);
        } else {
          setIsCameraReady(true);
        }
      }
    }
  }, [capturedImage, cameraModalOpen, cameraStream, openCameraStream]);

  const handleCameraModalClose = useCallback(() => {
    setCameraModalOpen(false);
    resetCapturedImage({ reopenStream: false });
    closeCameraStream();
  }, [closeCameraStream, resetCapturedImage]);

  const capturePhoto = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) {
      setCameraError('摄像头尚未初始化，请稍候再试。');
      return;
    }

    const video = videoRef.current;
    if (!isCameraReady || video.videoWidth === 0 || video.videoHeight === 0) {
      console.debug('[camera] capture blocked, ready:', isCameraReady, 'video size:', video.videoWidth, video.videoHeight);
      setCameraError('摄像头数据未就绪，请等待片刻。');
      return;
    }

    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    if (!context) {
      setCameraError('无法读取摄像头画面，请重试。');
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((result) => resolve(result), 'image/jpeg', 0.9),
    );

    if (!blob) {
      console.debug('[camera] canvas toBlob produced null');
      setCameraError('拍照失败，请重试。');
      return;
    }

    const file = new File([blob], `tcm-camera-${Date.now()}.jpg`, {
      type: 'image/jpeg',
    });

    if (capturedImage?.previewUrl) {
      URL.revokeObjectURL(capturedImage.previewUrl);
    }

    const previewUrl = URL.createObjectURL(blob);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    console.debug('[camera] captured image ready', {
      width: video.videoWidth,
      height: video.videoHeight,
      size: blob.size,
    });
    setCapturedImage({ file, previewUrl, dataUrl });
    setCameraError(null);
  }, [capturedImage, isCameraReady]);

  const handleImageFileSelection = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (event.target.value) {
        event.target.value = '';
      }
      if (!file) {
        return;
      }

      if (!file.type.startsWith('image/')) {
        setCameraError('请选择图片文件。');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setCameraError('图片大小不能超过 5MB。');
        return;
      }

      if (capturedImage?.previewUrl) {
        URL.revokeObjectURL(capturedImage.previewUrl);
      }

      const objectUrl = URL.createObjectURL(file);
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        setCapturedImage({
          file,
          previewUrl: objectUrl,
          dataUrl: typeof result === 'string' ? result : objectUrl,
        });
        setCameraError(null);
      };
      reader.onerror = () => {
        console.error('读取图片文件失败');
        URL.revokeObjectURL(objectUrl);
        setCameraError('读取图片文件失败，请重试。');
      };
      reader.readAsDataURL(file);
      closeCameraStream();
    },
    [capturedImage, closeCameraStream],
  );

  const beginCameraCapture = useCallback(
    async (payload?: Partial<CameraRequestMetadata> | ActiveCameraRequest | null) => {
      const request = buildCameraRequest(payload ?? activeCameraRequest);
      setActiveCameraRequest(request);

      console.debug('[camera] begin capture', request);
      setCameraModalOpen(true);
      const opened = await openCameraStream();
      if (opened) {
        console.debug('[camera] stream opened, show modal');
      } else {
        console.debug('[camera] stream unavailable, fallback to file upload');
      }
    },
    [activeCameraRequest, buildCameraRequest, openCameraStream],
  );

  const handleCameraRequestTrigger = useCallback(
    (metadata?: CameraRequestMetadata) => {
      console.debug('[camera] manual trigger', metadata);
      beginCameraCapture(metadata ?? activeCameraRequest ?? undefined);
    },
    [activeCameraRequest, beginCameraCapture],
  );

  const handleCameraRequestDecline = (metadata?: CameraRequestMetadata) => {
    console.debug('[camera] user declined capture', metadata);
    setActiveCameraRequest(null);
    setCameraRequestDeclined(true);
    setAutoCameraRequestIssued(true);

    const focusLabel = metadata?.focus ? CAMERA_FOCUS_LABELS[metadata.focus] : undefined;
    const declineText = focusLabel
      ? `我暂时不进行${focusLabel}影像采集，请直接继续诊断。`
      : '我暂时不使用摄像头，请直接继续诊断。';

    void handleSendMessage(declineText);
  };

  const handleSendMessage = useCallback(async (
    input: string,
    options?: { silent?: boolean; conversationIdOverride?: string | null },
  ) => {
    const trimmed = input.trim();
    if (!trimmed) return;

  const silent = Boolean(options?.silent);
  const previousInputValue = chatInputValue;
    const shouldSearchCommunity = !silent && trimmed.length >= 2;
    if (shouldSearchCommunity) {
      const requestId = Date.now();
      communitySearchRequestIdRef.current = requestId;
      if (isMountedRef.current) {
        setCommunitySearching(true);
      }

      const searchTerm = trimmed.slice(0, 160);
      void fetchCommunityKnowledge(searchTerm, { limitPosts: 4, limitComments: 2 })
        .catch((error) => {
          console.warn('社区知识检索失败', error);
        })
        .finally(() => {
          if (!isMountedRef.current) {
            return;
          }
          if (communitySearchRequestIdRef.current === requestId) {
            setCommunitySearching(false);
            communitySearchRequestIdRef.current = null;
          }
        });
    }

    setError(null);
    const prevStage = stage;
    const timestamp = new Date().toISOString();
    const userMessage: Message = {
      role: 'user',
      content: trimmed,
      timestamp,
      messageType: 'text',
    };

    if (!silent) {
      setChatInputValue('');
      setMessages((prev) => [...prev, userMessage]);
    }
    setLoading(true);

    try {
      const response = await sendDiagnosisMessage(trimmed, {
        conversationId:
          options?.conversationIdOverride ?? conversationId ?? undefined,
        patientInfo: patientInfo || undefined,
      });

      setConversationId(response.conversation_id);
      setStage(response.stage);

      if (response.herbs) {
        setHerbs(response.herbs);
      }

      if (response.diagnosis) {
        setDiagnosisSummary(response.diagnosis);
      }

      if ('prescription_validation' in response) {
        setPrescriptionValidation(response.prescription_validation ?? null);
      }

      if (
        hasAuth &&
        response.medical_record_id &&
        response.medical_record_id !== (patientInfo?.latest_medical_record?.id ?? latestRecord?.id ?? null)
      ) {
        loadPatientContext();
      }

      if (diagnosisSessionId) {
        const sessionUpdates: Record<string, unknown> = {};
        if (
          response.diagnosis &&
          response.diagnosis !== lastSyncedSessionFactsRef.current.diagnosis
        ) {
          sessionUpdates.syndrome_summary = response.diagnosis;
        }
        if (response.herbs && response.herbs.length > 0) {
          const herbsSignature = JSON.stringify(response.herbs);
          if (herbsSignature !== lastSyncedSessionFactsRef.current.herbs) {
            sessionUpdates.formula_recommendations = response.herbs;
          }
        }

        if (Object.keys(sessionUpdates).length > 0) {
          void syncDiagnosisSessionFacts(sessionUpdates, {
            diagnosis: response.diagnosis ?? null,
            herbs: response.herbs ?? [],
          });
        }
      }

      let prescriptionMetadata: PrescriptionMetadata | null = null;
      if (response.prescription_id && response.prescription_id !== latestPrescriptionId) {
        const fallbackApiUrl =
          response.prescription_api_url ||
          `${API_BASE_URL}/herb-library/prescriptions/${response.prescription_id}`;

        let prescriptionPath = response.prescription_url || '';
        if (prescriptionPath.startsWith('/app/prescriptions/')) {
          // already correct
        } else if (prescriptionPath.startsWith('/prescriptions/')) {
          prescriptionPath = `/app${prescriptionPath}`;
        } else {
          prescriptionPath = `/app/prescriptions/${response.prescription_id}`;
        }

        const searchSuffix = `?source=${encodeURIComponent(fallbackApiUrl)}`;
        const routeWithQuery = `${prescriptionPath}${searchSuffix}`;

        const frontendBase =
          typeof window !== 'undefined' && window.location?.origin
            ? window.location.origin
            : '';
        const absoluteFrontendUrl = frontendBase
          ? `${frontendBase}${routeWithQuery}`
          : routeWithQuery;

        const legacyBackendUrl =
          response.prescription_url && response.prescription_url.startsWith('http')
            ? response.prescription_url
            : undefined;

        prescriptionMetadata = {
          url: absoluteFrontendUrl,
          frontendUrl: routeWithQuery,
          backendUrl: legacyBackendUrl,
          apiUrl: fallbackApiUrl,
          prescription_id: response.prescription_id,
          title: response.prescription_title || 'AI定制处方',
        };
        setLatestPrescriptionId(response.prescription_id);
      }

      const { sanitizedText, request: parsedCameraRequest } = parseCameraRequest(
        response.response,
      );

      const assistantMessage: Message = {
        role: 'assistant',
        content: sanitizedText,
        timestamp: new Date().toISOString(),
        messageType: 'tcm_response',
      };

      const hasCameraEvidence = hasCameraEvidenceRef.current;

      const cameraMessages: Message[] = [];
      let cameraRequestToActivate: ActiveCameraRequest | null = null;

      if (parsedCameraRequest) {
        const request = buildCameraRequest(parsedCameraRequest);
        cameraRequestToActivate = request;
        cameraMessages.push({
          role: 'assistant',
          content: request.instructions,
          timestamp: new Date().toISOString(),
          messageType: 'camera_request',
          metadata: {
            requestId: request.requestId,
            focus: request.focus,
            instructions: request.instructions,
            prompt: request.prompt,
            autoOpen: request.autoOpen,
          },
        });
      }

      const stageTransitionEligible =
        !cameraRequestDeclined &&
        !autoCameraRequestIssued &&
        !hasCameraEvidence &&
        !cameraRequestToActivate &&
        (isObservationStage(prevStage) && !isObservationStage(response.stage));

      if (stageTransitionEligible) {
        const inferredFocus = inferCameraFocus(response.diagnosis ?? null, trimmed);
        const focusConfig = CAMERA_FOCUS_CONFIG[inferredFocus];
        const fallbackRequest = buildCameraRequest({
          focus: inferredFocus,
          instructions: focusConfig.instructions,
          prompt: focusConfig.prompt,
          autoOpen: false,
        });
        cameraRequestToActivate = fallbackRequest;
        cameraMessages.push({
          role: 'assistant',
          content: focusConfig.message,
          timestamp: new Date().toISOString(),
          messageType: 'camera_request',
          metadata: {
            requestId: fallbackRequest.requestId,
            focus: fallbackRequest.focus,
            instructions: fallbackRequest.instructions,
            prompt: fallbackRequest.prompt,
            autoOpen: false,
          },
        });
      }

      setMessages((prev) => {
        let updated = [...prev];
        if (sanitizedText) {
          updated = [...updated, assistantMessage];
        }
        if (prescriptionMetadata) {
          updated.push({
            role: 'assistant',
            content: prescriptionMetadata.title || 'AI定制处方',
            timestamp: new Date().toISOString(),
            messageType: 'prescription',
            metadata: prescriptionMetadata,
          });
        }
        if (cameraMessages.length > 0) {
          updated = [...updated, ...cameraMessages];
        }
        return updated;
      });

      if (cameraRequestToActivate) {
        setActiveCameraRequest(cameraRequestToActivate);
        setAutoCameraRequestIssued(true);
        if (cameraRequestToActivate.autoOpen) {
          beginCameraCapture(cameraRequestToActivate);
        }
      }
    } catch (err) {
      console.error('发送诊断消息失败', err);
      setError('发送消息失败，请稍后再试。');
      if (!silent) {
        setChatInputValue(previousInputValue);
      }
      setMessages((prev) => [
        ...prev,
        {
          role: 'system',
          content: '抱歉，诊断服务暂时不可用，请稍后再试。',
          timestamp: new Date().toISOString(),
          messageType: 'system',
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [
    stage,
    cameraRequestDeclined,
    autoCameraRequestIssued,
    hasAuth,
    patientInfo,
    latestRecord,
    loadPatientContext,
    buildCameraRequest,
    beginCameraCapture,
    conversationId,
    latestPrescriptionId,
    diagnosisSessionId,
    syncDiagnosisSessionFacts,
    setAutoCameraRequestIssued,
    setActiveCameraRequest,
    setHerbs,
    setDiagnosisSummary,
    setLatestPrescriptionId,
    setConversationId,
    setStage,
    setError,
    setLoading,
    setMessages,
    hasCameraEvidenceRef,
    chatInputValue,
  ]);

  useEffect(() => {
    handleSendMessageRef.current = handleSendMessage;
  }, [handleSendMessage]);

  const cleanupVoiceModeResources = useCallback(() => {
    if (voiceModeRafRef.current !== null) {
      window.cancelAnimationFrame(voiceModeRafRef.current);
      voiceModeRafRef.current = null;
    }
    const recorder = voiceModeRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch (error) {
        console.warn('停止语音模式录音失败', error);
      }
    }
    voiceModeRecorderRef.current = null;

    if (voiceModeStreamRef.current) {
      voiceModeStreamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (error) {
          console.warn('停止音频轨道失败', error);
        }
      });
      voiceModeStreamRef.current = null;
    }

    if (voiceModeAudioContextRef.current) {
      try {
        void voiceModeAudioContextRef.current.close();
      } catch (error) {
        console.warn('关闭音频上下文失败', error);
      }
      voiceModeAudioContextRef.current = null;
    }

    voiceModeAnalyserRef.current = null;
    voiceModeCollectingRef.current = false;
    voiceModeSegmentChunksRef.current = [];
    voiceModeRecentChunksRef.current = [];
    voiceModeQueueRef.current = [];
    voiceModeProcessingRef.current = false;
    voiceModeSegmentStartRef.current = null;
    voiceModeDataArrayRef.current = null;
    voiceModeLastSpeechRef.current = 0;
    clearVoiceAwaitingTimer();
  }, [clearVoiceAwaitingTimer]);

  const resetVoiceModeState = useCallback(() => {
    stopVoicePlayback();
    cleanupVoiceModeResources();
    clearVoiceAwaitingTimer();
    voiceModeQueueRef.current = [];
    voiceModeProcessingRef.current = false;
    voiceModeCollectingRef.current = false;
    voiceModeSegmentStartRef.current = null;
    voiceModeActivatedAtRef.current = null;
    voiceModeLastSpokenKeyRef.current = null;
    voiceModeEnabledRef.current = false;
    voiceModePhaseRef.current = 'inactive';
    setVoiceModeEnabled(false);
    setVoiceModePhase('inactive');
    setVoiceModeLevel(0);
    setVoiceModeLastTranscript(null);
    setVoiceModeError(null);
  }, [cleanupVoiceModeResources, clearVoiceAwaitingTimer, stopVoicePlayback]);

  const processVoiceQueue = useCallback(async () => {
    if (voiceModeProcessingRef.current || !voiceModeEnabledRef.current) {
      return;
    }
    const nextSegment = voiceModeQueueRef.current.shift();
    if (!nextSegment) {
      return;
    }

    voiceModeProcessingRef.current = true;
    voiceModePhaseRef.current = 'processing';
    setVoiceModePhase('processing');
    clearVoiceAwaitingTimer();

    try {
      const sessionIdForTranscript = diagnosisSession?.session_id ?? diagnosisSessionId ?? undefined;
      const result = await transcribeVoice(nextSegment, { sessionId: sessionIdForTranscript });
      const transcript = (result.transcript || '').trim();
      if (!transcript) {
        setVoiceModeError('未识别到有效语音，请再试一次。');
      } else {
        setVoiceModeError(null);
        setVoiceModeLastTranscript(transcript);
        const sendMessage = handleSendMessageRef.current;
        if (sendMessage) {
          await sendMessage(transcript);
        }
        if (voiceModeEnabledRef.current) {
          voiceModePhaseRef.current = 'awaiting_playback';
          voiceModeCollectingRef.current = false;
          voiceModeSegmentChunksRef.current = [];
          voiceModeSegmentStartRef.current = null;
          voiceModeRecentChunksRef.current = [];
          setVoiceModePhase('awaiting_playback');
          scheduleVoiceAwaitingTimeout();
        }
      }
    } catch (err) {
      console.error('语音模式处理失败', err);
      const message =
        err instanceof Error
          ? err.message
          : '语音识别失败，请稍后重试。';
      setVoiceModeError(message);
      voiceModePhaseRef.current = 'error';
      setVoiceModePhase('error');
      clearVoiceAwaitingTimer();
      window.setTimeout(() => {
        if (voiceModeEnabledRef.current && voiceModePhaseRef.current === 'error') {
          voiceModePhaseRef.current = 'listening';
          setVoiceModePhase('listening');
        }
      }, 1800);
    } finally {
      voiceModeProcessingRef.current = false;
      if (voiceModeEnabledRef.current && voiceModePhaseRef.current === 'processing') {
        voiceModePhaseRef.current = 'listening';
        setVoiceModePhase('listening');
      }
      if (voiceModeEnabledRef.current && voiceModeQueueRef.current.length > 0) {
        void processVoiceQueue();
      }
    }
  }, [clearVoiceAwaitingTimer, diagnosisSession?.session_id, diagnosisSessionId, scheduleVoiceAwaitingTimeout]);

  useEffect(() => {
    processVoiceQueueRef.current = () => {
      void processVoiceQueue();
    };
  }, [processVoiceQueue]);

  const finalizeVoiceSegment = useCallback(
    (endedAt: number, options?: { force?: boolean }) => {
      const chunks = voiceModeSegmentChunksRef.current.slice();
      voiceModeSegmentChunksRef.current = [];
      voiceModeCollectingRef.current = false;

      const segmentStart = voiceModeSegmentStartRef.current;
      voiceModeSegmentStartRef.current = null;

      if (!chunks.length) {
        return;
      }

      const durationMs = typeof segmentStart === 'number' ? endedAt - segmentStart : undefined;
      const force = Boolean(options?.force);
      if (!force && typeof durationMs === 'number' && durationMs < voiceModeSettingsRef.current.minUtteranceMs) {
        return;
      }

      const recorder = voiceModeRecorderRef.current;
      const mimeType = recorder?.mimeType || 'audio/webm';
      const blob = new Blob(chunks, { type: mimeType });
      if (blob.size < 2048) {
        return;
      }

      voiceModeQueueRef.current.push(blob);
      processVoiceQueueRef.current();
    },
    [],
  );

  const monitorVoiceModeLevel = useCallback(() => {
    if (!voiceModeEnabledRef.current) {
      return;
    }

    const analyser = voiceModeAnalyserRef.current;
    if (!analyser) {
      return;
    }

    const phase = voiceModePhaseRef.current;
    if (phase !== 'listening') {
      if (voiceModeCollectingRef.current) {
        voiceModeCollectingRef.current = false;
        voiceModeSegmentChunksRef.current = [];
        voiceModeSegmentStartRef.current = null;
      }
      if (phase === 'speaking') {
        voiceModeRecentChunksRef.current = [];
      }
      setVoiceModeLevel(0);
      voiceModeRafRef.current = window.requestAnimationFrame(monitorVoiceModeLevel);
      return;
    }

    let buffer = voiceModeDataArrayRef.current;
    if (!buffer || buffer.length !== analyser.fftSize) {
      buffer = new Float32Array(analyser.fftSize);
      voiceModeDataArrayRef.current = buffer;
    }

    analyser.getFloatTimeDomainData(buffer as any);

    let sumSquares = 0;
    for (let index = 0; index < buffer.length; index += 1) {
      const value = buffer[index];
      sumSquares += value * value;
    }

    const rms = Math.sqrt(sumSquares / buffer.length);
    const now = performance.now();

    if (Number.isFinite(rms) && now - voiceModeLastLevelUpdateRef.current > 80) {
      voiceModeLastLevelUpdateRef.current = now;
      setVoiceModeLevel(Number.isFinite(rms) ? rms : 0);
    }

    const { threshold, silenceMs, maxUtteranceMs } = voiceModeSettingsRef.current;

    if (rms > threshold) {
      voiceModeLastSpeechRef.current = now;
      if (!voiceModeCollectingRef.current) {
        voiceModeCollectingRef.current = true;
        voiceModeSegmentStartRef.current = now;
        const preRoll = voiceModeRecentChunksRef.current.slice(-2);
        voiceModeSegmentChunksRef.current = preRoll.slice();
      }
    } else if (voiceModeCollectingRef.current && now - voiceModeLastSpeechRef.current >= silenceMs) {
      finalizeVoiceSegment(now);
    }

    if (voiceModeCollectingRef.current && voiceModeSegmentStartRef.current !== null) {
      const duration = now - voiceModeSegmentStartRef.current;
      if (duration >= maxUtteranceMs) {
        finalizeVoiceSegment(now, { force: true });
      }
    }

    voiceModeRafRef.current = window.requestAnimationFrame(monitorVoiceModeLevel);
  }, [finalizeVoiceSegment]);

  const startVoiceMode = useCallback(async () => {
    if (voiceModeStartingRef.current) {
      return false;
    }
    voiceModeStartingRef.current = true;

    setVoiceModeError(null);
    voiceModePhaseRef.current = 'initializing';
    setVoiceModePhase('initializing');

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setVoiceModeError('当前浏览器不支持语音模式，请尝试更新或更换浏览器。');
        voiceModePhaseRef.current = 'error';
        setVoiceModePhase('error');
        return false;
      }

      cleanupVoiceModeResources();

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      voiceModeStreamRef.current = stream;

      const AudioContextConstructor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      const audioContext = AudioContextConstructor ? new AudioContextConstructor() : null;
      voiceModeAudioContextRef.current = audioContext;

      if (audioContext) {
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 1024;
        analyser.smoothingTimeConstant = 0.4;
        source.connect(analyser);
        voiceModeAnalyserRef.current = analyser;
        voiceModeDataArrayRef.current = new Float32Array(analyser.fftSize);
      } else {
        voiceModeAnalyserRef.current = null;
        voiceModeDataArrayRef.current = null;
      }

      voiceModeRecentChunksRef.current = [];
      voiceModeSegmentChunksRef.current = [];
      voiceModeQueueRef.current = [];
      voiceModeCollectingRef.current = false;
      voiceModeSegmentStartRef.current = null;
      voiceModeLastSpeechRef.current = performance.now();
      voiceModeLastLevelUpdateRef.current = 0;

      const mimeCandidates = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4',
      ];
      let recorder: MediaRecorder;
      const candidate = mimeCandidates.find((type) => {
        try {
          return MediaRecorder.isTypeSupported(type);
        } catch (error) {
          return false;
        }
      });
      if (candidate) {
        recorder = new MediaRecorder(stream, { mimeType: candidate });
      } else {
        recorder = new MediaRecorder(stream);
      }

      voiceModeRecorderRef.current = recorder;
      recorder.addEventListener('dataavailable', (event: BlobEvent) => {
        const { data } = event;
        if (!data || data.size === 0) {
          return;
        }
        if (voiceModePhaseRef.current === 'speaking') {
          voiceModeRecentChunksRef.current = [];
          voiceModeSegmentChunksRef.current = [];
          voiceModeSegmentStartRef.current = null;
          return;
        }
        voiceModeRecentChunksRef.current.push(data);
        if (voiceModeRecentChunksRef.current.length > 8) {
          voiceModeRecentChunksRef.current.shift();
        }
        if (voiceModeCollectingRef.current) {
          voiceModeSegmentChunksRef.current.push(data);
        }
      });

      recorder.start(250);
      voiceModeActivatedAtRef.current = Date.now();
      voiceModePhaseRef.current = 'listening';
      setVoiceModePhase('listening');
      voiceModeRafRef.current = window.requestAnimationFrame(monitorVoiceModeLevel);
      return true;
    } catch (err) {
      console.error('启动语音模式失败', err);
      const message =
        err instanceof DOMException && err.name === 'NotAllowedError'
          ? '未获得麦克风权限，请在浏览器设置中允许访问后重试。'
          : err instanceof Error
          ? err.message
          : '无法启动语音模式，请检查设备或权限。';
      setVoiceModeError(message);
      voiceModePhaseRef.current = 'error';
      setVoiceModePhase('error');
      cleanupVoiceModeResources();
      return false;
    } finally {
      voiceModeStartingRef.current = false;
    }
  }, [cleanupVoiceModeResources, monitorVoiceModeLevel]);

  const handleVoiceModeToggle = useCallback(async () => {
    if (voiceModeEnabledRef.current) {
      resetVoiceModeState();
      return;
    }

    const latestAssistant = [...messages]
      .map((message, index) => ({ message, index }))
      .reverse()
      .find(({ message }) => message.role === 'assistant' && message.content?.trim());

    if (latestAssistant) {
      voiceModeLastSpokenKeyRef.current = buildMessageKey(latestAssistant.message, latestAssistant.index);
    } else {
      voiceModeLastSpokenKeyRef.current = null;
    }

    voiceModeActivatedAtRef.current = Date.now();
    setVoiceModeError(null);
    setVoiceModeLastTranscript(null);
    setVoiceModeLevel(0);

    voiceModeEnabledRef.current = true;
    setVoiceModeEnabled(true);
    const started = await startVoiceMode();
    if (!started) {
      voiceModeEnabledRef.current = false;
      voiceModePhaseRef.current = 'inactive';
      setVoiceModeEnabled(false);
      setVoiceModePhase('inactive');
    }
  }, [buildMessageKey, messages, resetVoiceModeState, startVoiceMode]);

  const uploadCapturedImage = useCallback(async () => {
    if (!capturedImage) {
      setCameraError('请先拍摄一张清晰的照片。');
      return;
    }

    const request = buildCameraRequest(activeCameraRequest);
    setActiveCameraRequest(request);
    setCameraUploading(true);
    setCameraError(null);

    try {
      console.debug('[camera] uploading captured image', {
        name: capturedImage.file.name,
        size: capturedImage.file.size,
        request,
      });
      const formData = new FormData();
      formData.append('file', capturedImage.file);
      const focusValue = request.focus ?? activeCameraRequest?.focus ?? 'face';
      formData.append('focus', focusValue);

      const url = `/face-diagnosis${
        conversationId ? `?conversation_id=${encodeURIComponent(conversationId)}` : ''
      }`;

      const response = await apiClient.post<FaceDiagnosisResponse>(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const responseData = response.data;
      const diagnosisText: string = responseData?.response ?? '影像分析完成。';
      const returnedConversationId: string | undefined = responseData?.conversation_id;

      if (returnedConversationId && returnedConversationId !== conversationId) {
        console.debug('[camera] updated conversation id from analysis', returnedConversationId);
        setConversationId(returnedConversationId);
      }

      const timestamp = new Date().toISOString();
      const focusLabel = CAMERA_FOCUS_LABELS[request.focus] ?? '影像';
      const imageMessage: Message = {
        role: 'user',
        content: `已上传${focusLabel}影像，AI助手正在分析。`,
        timestamp,
        messageType: 'image',
        metadata: {
          requestId: request.requestId,
          focus: request.focus,
          prompt: request.prompt,
          reportId: responseData?.report?.report_id,
        },
      };

      setMessages((prev) => [...prev, imageMessage]);
      console.debug('[camera] analysis message appended');
      setActiveCameraRequest(null);
      setCameraModalOpen(false);
      resetCapturedImage({ reopenStream: false });

      if (diagnosisText) {
        await handleSendMessage(`【影像分析结果】
${diagnosisText}`, {
          silent: true,
          conversationIdOverride: returnedConversationId ?? conversationId,
        });
      }
    } catch (error) {
      console.error('上传摄像头图像失败', error);
      setCameraError('上传或分析失败，请稍后重试。');
    } finally {
      setCameraUploading(false);
      console.debug('[camera] upload flow finished');
      closeCameraStream();
    }
  }, [
    activeCameraRequest,
    buildCameraRequest,
    capturedImage,
    closeCameraStream,
    conversationId,
    handleSendMessage,
    resetCapturedImage,
  ]);

  const handleForwardAnalysis = useCallback(
    (analysis: string, metadata?: CameraAnalysisMetadata) => {
      const content = analysis.trim();
      if (!content) {
        return;
      }

      if (metadata?.requestId) {
        setMessages((prev) =>
          prev.map((msg) => {
            if (
              msg.messageType === 'image_analysis' &&
              msg.metadata &&
              (msg.metadata as CameraAnalysisMetadata)?.requestId === metadata.requestId
            ) {
              return {
                ...msg,
                metadata: {
                  ...(msg.metadata as CameraAnalysisMetadata),
                  forwarded: true,
                },
              };
            }
            return msg;
          }),
        );
      }

      handleSendMessage(`【影像分析结果】\n${content}`);
    },
    [handleSendMessage],
  );

  const handleCameraBannerAction = useCallback(() => {
    if (!cameraBannerConfig?.action) {
      return;
    }
    beginCameraCapture(activeCameraRequest);
  }, [activeCameraRequest, beginCameraCapture, cameraBannerConfig]);

  const handlePermissionBannerDismiss = useCallback(() => {
    setPermissionBannerDismissed(true);
  }, []);

  const handleStartNewConversation = useCallback(() => {
    closeCameraStream();
    resetCapturedImage({ reopenStream: false });
    setCameraModalOpen(false);
    setActiveCameraRequest(null);
    setAutoCameraRequestIssued(false);
    setCameraRequestDeclined(false);
    setCameraError(null);
    setCameraUploading(false);
    setCommunitySearching(false);
    stopVoicePlayback();
    const statuses = voicePlaybackStatusesRef.current;
    Object.values(statuses).forEach((status) => {
      if (status.audioUrl) {
        URL.revokeObjectURL(status.audioUrl);
      }
    });
    setVoicePlaybackStatuses({});
    activeVoiceKeyRef.current = null;
    setActiveVoiceKey(null);
    resetVoiceModeState();
    setChatInputValue('');
    communitySearchRequestIdRef.current = null;
    setMessages([createInitialAssistantMessage()]);
    setConversationId(null);
    setStage(DEFAULT_STAGE);
    setHerbs([]);
    setDiagnosisSummary(null);
    setLatestPrescriptionId(null);
    setDiagnosisSession(null);
    setDiagnosisSessionId(null);
  setPrescriptionValidation(null);
    setSessionFactsDraft({});
    setSessionError(null);
    lastSyncedSessionFactsRef.current = { diagnosis: '', herbs: '' };
    sessionInitializedRef.current = false;
    setError(null);
    setPermissionBannerDismissed(false);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(CHAT_STORAGE_KEY);
      } catch (storageError) {
        console.warn('清理旧会话失败', storageError);
      }
    }
    void initializeDiagnosisSession({ forceReset: true });
  }, [
    closeCameraStream,
    initializeDiagnosisSession,
    resetCapturedImage,
    resetVoiceModeState,
    setVoicePlaybackStatuses,
    stopVoicePlayback,
  ]);

  const renderStageProgress = () => (
    <ul className="diagnosis-stage-progress">
      {stageOrder.map((item, index) => {
        const effectiveIndex = activeStageIndex >= 0 ? activeStageIndex : 0;
        const statusClass =
          index < effectiveIndex
            ? 'completed'
            : index === effectiveIndex
            ? 'active'
            : 'upcoming';

        return (
          <li key={item} className={`stage-item ${statusClass}`}>
            <span className="stage-index">{index + 1}</span>
            <div className="stage-info">
              <p className="stage-label">{stageLabels[item]}</p>
              <small className="stage-description">
                {stageDescriptions[item]}
              </small>
            </div>
          </li>
        );
      })}
    </ul>
  );

  const renderPatientContext = () => {
    if (!hasAuth) {
      return (
        <div className="patient-context-card guest">
          <div>
            <h3>登录以同步问诊历史</h3>
            <p>完成登录后，系统会引用您的历史病历与处方，为AI辨证提供更充分的依据。</p>
          </div>
        </div>
      );
    }

    if (patientContextLoading) {
      return (
        <div className="patient-context-card loading">
          <h3>正在加载历史病历...</h3>
          <p>即将为您同步最近的问诊记录和处方。</p>
        </div>
      );
    }

    if (patientContextError) {
      return (
        <div className="patient-context-card error">
          <div>
            <h3>病历同步失败</h3>
            <p>{patientContextError}</p>
          </div>
          <button
            type="button"
            className="profile-secondary-button"
            onClick={loadPatientContext}
          >
            重试加载
          </button>
        </div>
      );
    }

    if (!patientProfile) {
      return null;
    }

    const healthSummaryText =
      patientProfile.user.health_conditions?.trim() || '未填写健康状况概述';
    const latestRecordDate = latestRecord?.updated_at || latestRecord?.created_at;
    const prescriptionsPreview = recentPrescriptions.slice(0, 3);

    return (
      <div className="patient-context-card">
        <div className="patient-context-header">
          <div>
            <h3>历史病历概览</h3>
            <p>最新病历与处方将自动参与AI辨证分析。</p>
          </div>
          <button
            type="button"
            className="profile-secondary-button"
            onClick={loadPatientContext}
          >
            刷新
          </button>
        </div>

        <div className="patient-context-stats">
          <div className="context-pill">
            <span>累计病历</span>
            <strong>{patientProfile.stats.total_medical_records}</strong>
          </div>
          <div className="context-pill">
            <span>历史处方</span>
            <strong>{patientProfile.stats.total_prescriptions}</strong>
          </div>
          <div className="context-pill">
            <span>健康概况</span>
            <strong>{healthSummaryText}</strong>
          </div>
        </div>

        <div className="patient-context-body">
          <div className="context-section">
            <div className="section-header">
              <span className="section-title">最近病历</span>
              {latestRecordDate && (
                <span className="section-time">
                  更新于 {new Date(latestRecordDate).toLocaleString()}
                </span>
              )}
            </div>
            {latestRecord ? (
              <>
                <p className="context-highlight">{latestRecord.summary || '未填写摘要'}</p>
                <p className="context-line">辨证：{latestRecord.diagnosis || '暂无诊断信息'}</p>
                <p className="context-line">症状：{latestRecord.symptoms || '未记录症状描述'}</p>
              </>
            ) : (
              <div className="empty-state subtle">暂无病历记录，建议先在个人中心完善病历信息。</div>
            )}
          </div>

          {prescriptionsPreview.length > 0 && (
            <div className="context-section">
              <div className="section-header">
                <span className="section-title">近期处方</span>
              </div>
              <ul className="context-list">
                {prescriptionsPreview.map((item) => (
                  <li key={item.id}>
                    <span className="context-list-title">{item.title || `处方 ${item.prescription_id}`}</span>
                    <span className="context-list-sub">
                      {item.created_at ? new Date(item.created_at).toLocaleDateString() : '未知时间'} ·{' '}
                      {item.diagnosis_summary || '未记录辨证'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderDiagnosisSessionPanel = () => {
    if (!ENABLE_ADVANCED_SESSION_PANEL) {
      return null;
    }
    if (sessionLoading && !diagnosisSession) {
      return (
        <div className="diagnosis-session-card loading">
          <h3>正在准备诊断流程...</h3>
          <p>系统正在加载四诊阶段，请稍候。</p>
        </div>
      );
    }

    if (sessionError && !diagnosisSession) {
      return (
        <div className="diagnosis-session-card error">
          <div>
            <h3>诊断阶段初始化失败</h3>
            <p>{sessionError}</p>
          </div>
          <div className="session-actions">
            <button type="button" onClick={() => initializeDiagnosisSession({ forceReset: true })}>
              重新创建诊断流程
            </button>
          </div>
        </div>
      );
    }

    if (!diagnosisSession) {
      return (
        <div className="diagnosis-session-card empty">
          <div>
            <h3>诊断阶段辅助面板</h3>
            <p>系统会记录望闻问切要点与随访任务，帮助您掌握问诊节奏。</p>
          </div>
          <div className="session-actions">
            <button type="button" onClick={() => initializeDiagnosisSession({ forceReset: true })}>
              初始化诊断流程
            </button>
          </div>
        </div>
      );
    }

    const currentSession = diagnosisSession;
    const factKeySet = new Set<string>([
      ...currentSession.required_facts,
      ...Object.keys(sessionFactsDraft),
    ]);
    const factKeys = Array.from(factKeySet).sort((a, b) => {
      const aIndex = DEFAULT_FACT_ORDER.indexOf(a);
      const bIndex = DEFAULT_FACT_ORDER.indexOf(b);
      if (aIndex === -1 && bIndex === -1) {
        return a.localeCompare(b);
      }
      if (aIndex === -1) {
        return 1;
      }
      if (bIndex === -1) {
        return -1;
      }
      return aIndex - bIndex;
    });

    const pendingQuestions = currentSession.pending_questions ?? [];
    const followUpTasks = currentSession.follow_up_tasks ?? [];
    const explanationCard = currentSession.explanation_card;

    const explanationSections = (
      [
        { key: 'symptoms_summary' as const, title: '症候提炼' },
        { key: 'pulse_overview' as const, title: '脉象观察' },
        { key: 'syndrome_reasoning' as const, title: '辨证推理' },
        { key: 'treatment_principles' as const, title: '治法原则' },
        { key: 'formula_and_acupoints' as const, title: '方药与穴位' },
        { key: 'lifestyle_plan' as const, title: '起居调护' },
        { key: 'dietary_advice' as const, title: '饮食调理' },
        { key: 'risk_alerts' as const, title: '风险提示' },
      ] satisfies Array<{ key: keyof DiagnosisExplanationCard; title: string }>
    )
      .map((section) => ({
        ...section,
        value: explanationCard?.[section.key],
      }))
      .filter((section) => hasRenderableValue(section.value));

    const stageSummariesEntries = explanationCard?.stage_summaries
      ? Object.entries(explanationCard.stage_summaries)
          .filter(([, value]) => hasRenderableValue(value))
          .sort(([stageA], [stageB]) => {
            const indexA = DIAGNOSIS_STAGE_ORDER.indexOf(stageA);
            const indexB = DIAGNOSIS_STAGE_ORDER.indexOf(stageB);
            if (indexA === -1 && indexB === -1) {
              return stageA.localeCompare(stageB);
            }
            if (indexA === -1) {
              return 1;
            }
            if (indexB === -1) {
              return -1;
            }
            return indexA - indexB;
          })
      : [];

    const mergedFollowUpTasks = (() => {
      const cardFollowUps = (explanationCard?.follow_up_tasks ?? []).filter((task) => task);
      const manualFollowUps = followUpTasks.filter((task) => task);
      const seen = new Set<string>();
      return [...cardFollowUps, ...manualFollowUps].filter((task) => {
        const signature = `${task.title ?? ''}|${task.due_date ?? ''}|${task.channel ?? ''}`;
        if (seen.has(signature)) {
          return false;
        }
        seen.add(signature);
        return true;
      });
    })();

    return (
      <div className={`diagnosis-session-card${sessionLoading ? ' syncing' : ''}`}>
        <div className="session-header">
          <div>
            <h3>{currentSession.stage_name}</h3>
            <p>{currentSession.stage_description}</p>
          </div>
          <div className="session-actions">
            <button type="button" onClick={handleRefreshSession} disabled={sessionLoading}>
              刷新阶段
            </button>
            <button
              type="button"
              onClick={() => handleAdvanceStage(false)}
              disabled={sessionLoading || currentSession.is_final}
            >
              推进阶段
            </button>
            <button
              type="button"
              className="session-action-secondary"
              onClick={() => handleAdvanceStage(true)}
              disabled={sessionLoading || currentSession.is_final}
            >
              强制推进
            </button>
          </div>
        </div>

        <div className="session-body">
          {pendingQuestions.length > 0 && (
            <div className="session-section">
              <h4>待补充问题</h4>
              <ul>
                {pendingQuestions.map((question) => (
                  <li key={question}>{question}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="session-section">
            <h4>阶段要点</h4>
            {factKeys.length === 0 ? (
              <p className="session-empty">本阶段暂无结构化字段，可直接推进下一步。</p>
            ) : (
              <div className="session-fact-grid">
                {factKeys.map((key) => (
                  <label className="session-fact-field" key={key}>
                    <span className="session-fact-label">{formatFactLabel(key)}</span>
                    <textarea
                      className="session-fact-input"
                      placeholder={`请填写${formatFactLabel(key)}…`}
                      value={sessionFactsDraft[key] ?? ''}
                      onChange={(event) => handleSessionFactChange(key, event.target.value)}
                      rows={Math.max(3, Math.min(8, (sessionFactsDraft[key] ?? '').split('\n').length))}
                    />
                  </label>
                ))}
              </div>
            )}
            <div className="session-actions">
              <button
                type="button"
                onClick={handleSubmitSessionFacts}
                disabled={sessionLoading || factKeys.length === 0}
              >
                提交阶段信息
              </button>
              <button
                type="button"
                className="session-action-secondary"
                onClick={() => syncSessionDraft(currentSession, { overwrite: true })}
                disabled={sessionLoading}
              >
                重置表单
              </button>
            </div>
          </div>

          {stageSummariesEntries.length > 0 && (
            <div className="session-section">
              <h4>阶段回顾</h4>
              <ol className="session-timeline">
                {stageSummariesEntries.map(([stageKey, summary], index) => (
                  <li className="session-timeline-item" key={stageKey}>
                    <div className="timeline-node">
                      <span className="timeline-index">{index + 1}</span>
                    </div>
                    <div className="timeline-card">
                      <div className="timeline-heading">
                        <span className="timeline-stage">{formatStageLabel(stageKey)}</span>
                      </div>
                      <div className="timeline-body">{renderRichValue(summary)}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {explanationSections.length > 0 && (
            <div className="session-section">
              <h4>阶段解析</h4>
              <div className="session-card-grid">
                {explanationSections.map((section) => (
                  <div className="session-info-card" key={section.key}>
                    <div className="session-card-header">
                      <span className="session-chip">{section.title}</span>
                    </div>
                    <div className="session-card-body">{renderRichValue(section.value)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {mergedFollowUpTasks.length > 0 && (
            <div className="session-section">
              <h4>随访日程</h4>
              <ol className="session-timeline session-task-timeline">
                {mergedFollowUpTasks.map((task, index) => (
                  <li className="session-timeline-item" key={`${task.title ?? 'task'}-${index}`}>
                    <div className="timeline-node">
                      <span className="timeline-index">{index + 1}</span>
                    </div>
                    <div className="timeline-card">
                      <div className="timeline-heading">
                        <strong>{task.title || `随访事项 ${index + 1}`}</strong>
                        {task.due_date && (
                          <span className="timeline-tag">{new Date(task.due_date).toLocaleString()}</span>
                        )}
                      </div>
                      {task.description && (
                        <div className="timeline-body">{renderRichValue(task.description)}</div>
                      )}
                      {(task.channel || task.category) && (
                        <div className="timeline-meta">
                          {task.channel && <span>渠道：{task.channel}</span>}
                          {task.category && <span>类别：{task.category}</span>}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {sessionError && <div className="session-error-banner">{sessionError}</div>}
        </div>
      </div>
    );
  };

  return (
    <div className="unified-page-wrapper">
      <header className="unified-page-header">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h2>AI中医问诊室</h2>
        </motion.div>
        <motion.div
          className="diagnosis-mode-indicator"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <span className="diagnosis-icon">🩺</span>
          <span className="diagnosis-text">
            当前阶段：{stageLabels[stage]}
          </span>
        </motion.div>
        <motion.button
          type="button"
          className="new-conversation-button"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.15 }}
          onClick={handleStartNewConversation}
        >
          新建问诊对话
        </motion.button>
      </header>

      <section className="patient-context-section">{renderPatientContext()}</section>

      <section className="stage-progress-container">{renderStageProgress()}</section>

      {ENABLE_ADVANCED_SESSION_PANEL && (
        <section className="diagnosis-session-section">{renderDiagnosisSessionPanel()}</section>
      )}

      {cameraBannerConfig && (
        <div className={`camera-permission-banner ${cameraBannerConfig.tone}`}>
          <div className="camera-banner-info">
            <span className="camera-banner-icon">
              {cameraBannerConfig.tone === 'info' ? '📸' : '⚠️'}
            </span>
            <div className="camera-banner-text">
              <strong>{cameraBannerConfig.title}</strong>
              <p>{cameraBannerConfig.message}</p>
            </div>
          </div>
          <div className="camera-banner-controls">
            {cameraBannerConfig.action && cameraBannerConfig.actionLabel && (
              <button
                type="button"
                className="camera-banner-action"
                onClick={handleCameraBannerAction}
              >
                {cameraBannerConfig.actionLabel}
              </button>
            )}
            <button
              type="button"
              className="camera-banner-dismiss"
              onClick={handlePermissionBannerDismiss}
              aria-label="关闭摄像头权限提示"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <section className="diagnosis-summary-wrapper">
        {diagnosisSummary && (
          <motion.div
            className="diagnosis-summary-card"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h4>辨证结论</h4>
            <p>{diagnosisSummary}</p>
          </motion.div>
        )}

        {herbs.length > 0 && (
          <motion.div
            className="herb-summary-card"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h4>药材建议</h4>
            <ul>
              {herbs.map((herb) => (
                <li key={`${herb.name}-${herb.dosage}`.replace(/\s+/g, '')}>
                  <strong>{herb.name}</strong>
                  {herb.dosage ? ` · ${herb.dosage}` : ''}
                  {herb.reason ? ` — ${herb.reason}` : ''}
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </section>

      <div className="unified-page-content-scrollable">
        <MessageList
          messages={messages}
          onCameraCaptureRequest={handleCameraRequestTrigger}
          onCameraRequestDecline={handleCameraRequestDecline}
          onForwardAnalysis={handleForwardAnalysis}
          onToggleVoicePlayback={handleToggleVoicePlayback}
          voicePlaybackStatuses={voicePlaybackStatuses}
          activeVoiceKey={activeVoiceKey}
          getMessageKey={buildMessageKey}
        />
        {communitySearching && (
          <div className="community-searching-indicator" role="status" aria-live="polite">
            <span className="community-searching-spinner" aria-hidden />
            <span className="community-searching-text">正在搜索社区经验…</span>
          </div>
        )}
        {loading && (
          <div className="typing-container">
            <div className="typing-indicator">
              <span />
            </div>
            <span className="typing-caption">AI助手正在生成应答…</span>
          </div>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="chat-input-area">
        <ChatInput
          onSendMessage={handleSendMessage}
          disabled={loading}
          onOpenCamera={() => beginCameraCapture(activeCameraRequest)}
          cameraRequested={Boolean(activeCameraRequest)}
          cameraDisabled={cameraPermission === 'denied' || cameraPermission === 'unsupported'}
          value={chatInputValue}
          onChangeValue={setChatInputValue}
        />
        <div className="voice-control-panel">
          <button
            type="button"
            className={`voice-record-button${voiceModeEnabled ? ' active' : ''}`}
            onClick={() => void handleVoiceModeToggle()}
            aria-pressed={voiceModeEnabled}
          >
            <span className="voice-record-icon" aria-hidden>
              🎤
            </span>
            <span>{voiceModeEnabled ? '关闭语音模式' : '开启语音模式'}</span>
          </button>

          {voiceModeEnabled && voiceModePhase === 'initializing' && (
            <div className="voice-transcribing-banner" role="status" aria-live="polite">
              <span className="voice-spinner" aria-hidden />
              <span>语音模式启动中，请稍候…</span>
            </div>
          )}

          {voiceModeEnabled && voiceModePhase === 'listening' && (
            <div className="voice-recording-banner" role="status" aria-live="assertive">
              <div
                className="voice-meter"
                aria-hidden
                style={{ opacity: 0.25 + Math.min(voiceModeLevel * 2, 0.6) }}
              />
              <div className="voice-recording-info">
                <strong>持续监听中</strong>
                <span>实时音量：{voiceLevelPercent}%</span>
                <span>检测到停顿后将自动发送</span>
              </div>
            </div>
          )}

          {voiceModeEnabled && voiceModePhase === 'processing' && (
            <div className="voice-transcribing-banner" role="status" aria-live="polite">
              <span className="voice-spinner" aria-hidden />
              <span>正在理解刚才的语音…</span>
            </div>
          )}

          {voiceModeEnabled && voiceModePhase === 'error' && (
            <div className="voice-error-banner" role="alert">
              <div className="voice-error-text">
                语音模式异常：{voiceModeError ?? '请检查麦克风或稍后重试。'}
              </div>
              <div className="voice-error-actions">
                <button type="button" onClick={() => void handleVoiceModeToggle()}>
                  重新开启
                </button>
                <button type="button" className="voice-cancel-button" onClick={resetVoiceModeState}>
                  关闭语音
                </button>
              </div>
            </div>
          )}

          {voiceModeLastTranscript && voiceModePhase !== 'processing' && voiceModePhase !== 'error' && (
            <div className="voice-ready-banner" role="status" aria-live="polite">
              <div className="voice-ready-main">
                <div className="voice-ready-icon" aria-hidden>
                  ✅
                </div>
                <div className="voice-ready-content">
                  <strong>刚刚发送</strong>
                  <p className="voice-ready-text">{voiceModeLastTranscript}</p>
                  <span className="voice-ready-hint">继续讲话或使用文字输入补充内容。</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {cameraModalOpen && (
        <div className="camera-overlay" role="dialog" aria-modal="true">
          <div className="camera-modal">
            <div className="camera-modal-header">
              <div>
                <h3>图像上传中心</h3>
                <p>
                  {(activeCameraRequest?.instructions ||
                    '请根据指引调整姿势和光线，确保影像清晰。')}
                  <br />
                  <span className="camera-modal-secondary">也可以直接选择本地图片进行上传。</span>
                </p>
              </div>
              <button
                type="button"
                className="camera-modal-close"
                onClick={handleCameraModalClose}
                aria-label="关闭摄像头弹窗"
              >
                ×
              </button>
            </div>

            <div className="camera-modal-body">
              <div className="camera-video-wrapper">
                <video ref={videoRef} autoPlay playsInline muted />
                {!isCameraReady && (
                  <div className="camera-status-overlay">摄像头准备中...</div>
                )}
                <canvas ref={canvasRef} style={{ display: 'none' }} />
              </div>

              <div className="camera-upload-section">
                <div className="camera-upload-divider" aria-hidden="true">
                  <span>或</span>
                </div>
                <label className="camera-upload-button">
                  <input type="file" accept="image/*" onChange={handleImageFileSelection} />
                  从设备选择图片
                </label>
                <p className="camera-upload-hint">支持 JPG / PNG，文件大小不超过 5MB。</p>
              </div>

              {capturedImage && (
                <div className="camera-captured">
                  <div className="camera-captured-preview">
                    <img src={capturedImage.previewUrl} alt="捕捉预览" />
                  </div>
                  <button
                    type="button"
                    className="camera-retake-button"
                    onClick={() => resetCapturedImage({ reopenStream: true })}
                    disabled={cameraUploading}
                  >
                    重拍
                  </button>
                </div>
              )}

              {cameraError && <p className="camera-error">{cameraError}</p>}

              {cameraPermission === 'denied' && (
                <div className="camera-permission-hint">
                  <strong>摄像头权限已被关闭。</strong>
                  <span>请在浏览器地址栏左侧的锁形图标中开启摄像头权限后重试。</span>
                </div>
              )}
            </div>

            <div className="camera-modal-footer">
              <button
                type="button"
                className="camera-action-button"
                onClick={capturePhoto}
                disabled={cameraUploading || !isCameraReady}
              >
                📸 {isCameraReady ? '捕捉影像' : '准备中...'}
              </button>
              <button
                type="button"
                className="camera-action-button primary"
                onClick={uploadCapturedImage}
                disabled={!capturedImage || cameraUploading}
              >
                {cameraUploading ? '上传中…' : '上传至AI分析'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatInterface;
